// ENG-5858: pure decision function — should the manager restart an agent's
// persistent session because the effective sender_policy changed?
//
// Background: the ENG-5836 epic wires sender_policy through /host/refresh →
// manager → writeChannelCredentials → .mcp.json env vars. The manager's
// CHANNEL_WRITE_VERSION hash correctly invalidates on a policy change and
// the adapter rewrites the file. The gap that this decision closes is the
// last hop: the slack-channel / teams-channel MCP child reads
// SLACK_SENDER_POLICY / MSTEAMS_SENDER_POLICY from process.env exactly
// once at boot, so a rewrite of .mcp.json on disk doesn't reach the
// running child. Two existing restart paths refuse to help:
//
//   1. decideChannelRestart (channel-restart-decision.ts) only fires on
//      channel set membership change. Same channel + new env → no restart.
//   2. The .mcp.json value-only drift watcher (manager-worker.ts ~1029)
//      explicitly suppresses restart on value-only drift to stop Composio
//      URL rotation flapping the breaker (ENG-5537).
//
// Both suppressions are correct for their original purpose. This decision
// adds an orthogonal trigger keyed on the sender_policy hash itself, so
// composio URL rotation can't accidentally fire it (the hash sees only
// sender_policy, not the wider channel config or .mcp.json).
//
// Pure helper for unit testability, mirroring decideChannelRestart.

export interface SenderPolicyRestartDecisionInput {
  /**
   * Hash of the effective sender_policy from the *previous* refresh
   * tick, or `undefined` on first poll for this agent. On first poll
   * there's no prior state to compare against — the live session (if
   * any) either hasn't started yet or was spawned with the freshly
   * written env, so a restart would be a noisy no-op.
   */
  previousHash: string | undefined;
  /**
   * Hash of the effective sender_policy from this refresh tick. May be
   * the literal string of `null` (canonicalised) — that's a meaningful
   * value (org/agent cleared their policy) and must compare equal to
   * itself on subsequent ticks.
   */
  currentHash: string;
  /**
   * Agent's `session_mode` from the refresh response. Restart only
   * applies to `persistent` sessions — oneshot agents have no long-
   * lived MCP child whose env needs refreshing.
   */
  sessionMode: string | undefined;
  /**
   * Agent's framework. The sender_policy env vars are consumed by
   * MCP servers launched through Claude Code's .mcp.json mechanism;
   * other frameworks (openclaw, nemoclaw) don't use that path and
   * the restart doesn't apply.
   */
  framework: string;
  /**
   * Whether the agent's tmux session is currently alive. If it isn't,
   * there's nothing to restart — the manager will spawn a fresh one
   * downstream which reads the freshly-written .mcp.json env.
   */
  sessionHealthy: boolean;
  /**
   * Whether a channel-set restart was already scheduled for this agent
   * on this same tick. If so, that restart will pick up the new
   * sender_policy env for free — scheduling a second one would just
   * coalesce in `scheduleSessionRestart` and overwrite the friendlier
   * "channels added/removed" notice with a less informative one.
   */
  channelSetRestartAlreadyScheduled: boolean;
}

export interface SenderPolicyRestartDecision {
  /** True iff the manager should restart the session. */
  restart: boolean;
  /** Set to true when previousHash was undefined (informational). */
  firstPoll: boolean;
  /** Set to true when previousHash !== currentHash (informational). */
  changed: boolean;
}

export function decideSenderPolicyRestart(
  input: SenderPolicyRestartDecisionInput,
): SenderPolicyRestartDecision {
  const {
    previousHash,
    currentHash,
    sessionMode,
    framework,
    sessionHealthy,
    channelSetRestartAlreadyScheduled,
  } = input;

  // First poll — establish baseline, never restart.
  if (previousHash === undefined) {
    return { restart: false, firstPoll: true, changed: false };
  }

  // No change — debounces same-policy hashes that recompute every tick.
  if (previousHash === currentHash) {
    return { restart: false, firstPoll: false, changed: false };
  }

  // Restart only applies to live persistent claude-code sessions.
  if (sessionMode !== 'persistent') {
    return { restart: false, firstPoll: false, changed: true };
  }
  if (framework !== 'claude-code') {
    return { restart: false, firstPoll: false, changed: true };
  }
  if (!sessionHealthy) {
    return { restart: false, firstPoll: false, changed: true };
  }

  // A channel-set restart already scheduled this tick will respawn the
  // MCP children with the freshly-written env — no second restart needed.
  if (channelSetRestartAlreadyScheduled) {
    return { restart: false, firstPoll: false, changed: true };
  }

  return { restart: true, firstPoll: false, changed: true };
}
